import React, { memo, useRef } from "react";
import { RCCCheckBox, RCCGrid, RCCIcon } from "react-common-components";
import List from "src/components/List";
import { useTranslation } from "src/context/Translations/context";
import formatNumber from "src/utils/formatNumber";
import { v4 } from "uuid";
import { ReactComponent as Target } from "src/assets/icons/target.svg";
import InfiniteScrollWithoutFetch from "src/components/InfiniteScrollWithoutFetch";
import { useAuth } from "src/context/Auth/context";
import { IndicatorShowValues } from "../../interface";
import { IIndicatorsLevel, IIndicatorsValues, IListIndicators } from "../interface";
import { IListIndicatorsComponent } from "./interface";
import OverflowSpanBold, {ArrowLeft, ArrowRight, CardIndicatorPanel, DivAlignCenterWithOverflow, DivFlex, DivValues, IndicatorsPanelHeader, OverflowSpan} from "./styles";
import { useIndicatorsPanel } from "../../indicatorsPanelContext";

const SCROLL_AMOUNT = 152;

const formatTheNumbers = (number: number, currentLanguage?: string, decPlaces?: number) => {
	let sufix = "";
	let numberToFormat = number;
	if(Math.abs(number) > 999999){
		sufix = "M";
		numberToFormat = number / 1000000;
	}
	else if(Math.abs(number) > 999){
		sufix = "m";
		numberToFormat = number / 1000;
	}
	const numberFormatted = formatNumber(numberToFormat, currentLanguage, decPlaces);
	return `${numberFormatted}${sufix}`;
};

const getValue = (hasValue: boolean, value: IIndicatorsValues, showValues: IndicatorShowValues, iqc001Mostrarvalor: number, currentLanguage: string, iqc001Decimaispainel: number = 2) => {
	if(!hasValue)
		return "-";
	switch(showValues){
	case IndicatorShowValues.ACCORDING_TO_INDICATOR:
		if(iqc001Mostrarvalor.toString() === IndicatorShowValues.ABSOLUTE_VALUE)
			return formatTheNumbers(value.value, currentLanguage, iqc001Decimaispainel);
		return `${formatNumber(value.percentage, currentLanguage, iqc001Decimaispainel)} %`;
	case IndicatorShowValues.ABSOLUTE_VALUE:
		return formatTheNumbers(value.value, currentLanguage, iqc001Decimaispainel);
	default:
		return `${formatNumber(value.percentage, currentLanguage, iqc001Decimaispainel)} %`;
	}
};

export const colLevel = (level: IIndicatorsLevel, translate: (text: string, objKeys?: object | undefined) => string) => {
	switch(level){
	case IIndicatorsLevel.OPERATIONAL:
		return <span title={translate("Operacional")}><RCCIcon  color="var(--orange)" size={14} name="tools" style={{ marginRight: "0.25rem" }} /></span>;
	case IIndicatorsLevel.STRATEGIC:
		return <span title={translate("Estratégico")}>
			<Target style={{ marginRight: "0.25rem", width: "14px", height: "14px" }} />
		</span>;
	default:
		return <span title={translate("Tático")}><RCCIcon  color="var(--blue)" size={14} name="chessKnight" style={{ marginRight: "0.25rem" }} /></span>;
	}
};

const ListIndicators: React.FC<IListIndicatorsComponent> = ({
	data,
	options,
	showValues,
	filterList,
	showHierarchyList,
	loadChildrens,
	openChartIndicators,
	childrenLevel,
	openValuesOfPeriodIndicator,
	defaultChecked,
	mouseOutCardValuePanel,
	mouseOverCardValuePanel
}) => {
	const { translate, currentLanguage } = useTranslation();
	const { administrator, superUser } = useAuth();
	const {showCheckboxes, addSelectedCheckboxId, selectedCheckboxesId } = useIndicatorsPanel();
	const indexOfCurrentPeriod = data[0].values.findIndex((dt) => dt.currentPeriod === dt.labelCode);
	const translateX = useRef<number>(indexOfCurrentPeriod > 12 ? -72 * indexOfCurrentPeriod : 0);
	const translateXAccumulated = useRef<number>(0);
	const divRef = useRef<HTMLDivElement>(null);

	const handleScrollValues = (direction: "left" | "right") => {
		if(!divRef.current)
			return;

		const divs = divRef.current.querySelectorAll(".indicators-values-panel-cards");

		if(direction === "right")
			translateX.current -= SCROLL_AMOUNT;
		else
			translateX.current += SCROLL_AMOUNT;

		for(let i = 0; i < divs.length; i+=1){
			const div = divs[i] as HTMLDivElement;
			div.style.transform = `translateX(${translateX.current}px)`;
		}
	};

	const handleScrollAccumulatedValues = (direction: "left" | "right") => {
		if(!divRef.current)
			return;

		const divs = divRef.current.querySelectorAll(".indicators-accumulated-values-panel-cards");

		if(direction === "right")
			translateXAccumulated.current -= SCROLL_AMOUNT;
		else
			translateXAccumulated.current += SCROLL_AMOUNT;

		for(let i = 0; i < divs.length; i+=1){
			const div = divs[i] as HTMLDivElement;
			div.style.transform = `translateX(${translateXAccumulated.current}px)`;
		}
	};

	const colIndicator = (row: any) => (
		<DivAlignCenterWithOverflow childrenLevel={childrenLevel}>
			{colLevel(row.iqc001Nivel, translate)}
			{showCheckboxes && (
				<RCCCheckBox
					name={`selectIndicator-${v4()}`}
					onChange={() => addSelectedCheckboxId(row.iqc001Id)}
					defaultChecked={defaultChecked || selectedCheckboxesId.includes(row.iqc001Id)}
					styleInline={{ checkbox: "margin: 0; margin-right: 2px; margin-left: -0.8rem;" }}
				/>
			)}
			{showHierarchyList && row.hasChildren && (
				<RCCIcon 
					name={row.children && row.children.length > 0 ? "arrowDown" : "arrowRight"} 
					size={24} 
					style={{ cursor: "pointer" }} 
					onClick={() => loadChildrens(row.iqc001Id, row.children && row.children.length > 0 && true)}
				/>
			)}
			<OverflowSpanBold clickHoverEffect title={row.iqc001Descricao} onClick={() => openChartIndicators(row.iqc001Id, row.iqc001Descricao, row.accumulatedValues)}> 
				{row.iqc001Descricao} 
			</OverflowSpanBold>
		</DivAlignCenterWithOverflow>
	);

	const customHeaderColIndicator = (rows: any) => {
		const firstRow = rows[0];
		return(
			<DivAlignCenterWithOverflow childrenLevel={childrenLevel}>
				<OverflowSpan title={`${translate("Tipo de período")}: ${firstRow.iqb003Descricao}`}>{translate("Tipo de período")}: {firstRow.iqb003Descricao}</OverflowSpan>
			</DivAlignCenterWithOverflow>
		);};

	const colValues = (row: any) => (
		<DivValues borderRight>
			<RCCGrid 
				container
				item
				styleInline={`flex-wrap: unset !important; padding: 0 4px; transform: translateX(${translateX.current}px);`}
				justifyContent="space-between"
				className="indicators-values-panel-cards"
			>
				<InfiniteScrollWithoutFetch 
					listElements={row.values}
					mapper={(value: IIndicatorsValues) => (
						<CardIndicatorPanel
							key={v4()}
							onMouseOver={() => mouseOverCardValuePanel(value, row.iqc001Descricao, row.iqc001Formaaval, row.metrics)}
							onMouseOut={mouseOutCardValuePanel}
							color={value.color === null ? "var(--main-color)" : `#${value.color}`}
							hasValue={value.hasValue}
							currentPeriod={!!(value.labelCode && value.currentPeriod && value.labelCode === value.currentPeriod)}
							isDisabled={value.isDisabled}
							onClick={() => {
								mouseOutCardValuePanel();
								if(!openValuesOfPeriodIndicator || !value.periodId || (!administrator && !superUser && row.permission < 2))
									return;
								openValuesOfPeriodIndicator(row.iqc001Id, row.iqc001Descricao, value.periodId);
							}}
						>
							<div className="progress" style={{minWidth: "10%", width: value.percentage > 100 ? "100%" : `${value.percentage}%`}}/>
							<OverflowSpan>
								{value.isDisabled ? <RCCIcon color="var(--default-color-font)" name="close" size={18} /> : getValue(value.hasValue, value, showValues, row.iqc001Mostrarvalor, currentLanguage, row.iqc001Decimaispainel)}
							</OverflowSpan>
							{!value.isDisabled && !value.hasFca && value.fcaAbsent && (
								<RCCIcon 
									color="var(--orange)"
									name="exclamationCircle"
									size={12}
									style={{ 
										position: "absolute", 
										top: "-4px", 
										right: "-7px", 
										border: "1px solid #FFF",
										borderRadius: "50%",
										zIndex: 2
									}}
								/>
							)}
						</CardIndicatorPanel>
					)}
					defaultNumberOfElements={24}
					startIndex={indexOfCurrentPeriod > 12 ? indexOfCurrentPeriod : 0}
				/>
			</RCCGrid>
		</DivValues>
	);

	const customHeaderColValues = (rows: any) => {
		const firstRow = rows[0];
		return(
			<DivValues borderRight>
				<ArrowLeft className="arrowLeft-indicators">
					<RCCIcon 
						name="arrowLeft" 
						color="var(--default-color-font)" 
						size={32}
						onClick={() => handleScrollValues("left")} 
					/>
				</ArrowLeft>
				<RCCGrid 
					container
					item
					styleInline={`flex-wrap: unset !important; padding: 0 4px; transform: translateX(${translateX.current}px);`}
					justifyContent="space-between"
					className="indicators-values-panel-cards"
				>
					<InfiniteScrollWithoutFetch 
						listElements={firstRow.values} 
						mapper={(value: IIndicatorsValues) => (
							<IndicatorsPanelHeader
								key={v4()}
								currentPeriod={!!(value.labelCode && value.currentPeriod && value.labelCode === value.currentPeriod)}
							>
								<OverflowSpan title={value.label}>
									{value.label}
								</OverflowSpan>
							</IndicatorsPanelHeader>
						)}
						defaultNumberOfElements={24}
						startIndex={indexOfCurrentPeriod > 12 ? indexOfCurrentPeriod : 0}
					/>
				</RCCGrid>
				<ArrowRight className="arrowRight-indicators">
					<RCCIcon 
						name="arrowRight" 
						color="var(--default-color-font)" 
						size={32}
						onClick={() => handleScrollValues("right")} 
					/>
				</ArrowRight>
			</DivValues>
		);};

	const colAccumulatedValues = (row: any) => (
		<DivValues>
			<RCCGrid 
				container
				item
				styleInline="flex-wrap: unset !important; padding: 0 4px;"
				className="indicators-accumulated-values-panel-cards"
			>
				{row.accumulatedValues && row.accumulatedValues.length > 0 && row.accumulatedValues.map((value: IIndicatorsValues) => (
					<CardIndicatorPanel
						key={v4()}
						onMouseOver={() => mouseOverCardValuePanel(value, row.iqc001Descricao, row.iqc001Formaaval, row.metrics)}
						onMouseOut={mouseOutCardValuePanel}
						color={value.color === null ? "var(--main-color)" : `#${value.color}`}
						hasValue={value.hasValue}
						notHover
						currentPeriod={!!(value.labelCode && value.currentPeriod && value.labelCode === value.currentPeriod)}
					>
						<div className="progress" style={{minWidth: "10%", width: value.percentage > 100 ? "100%" : `${value.percentage}%`}}/>
						<OverflowSpan>
							{getValue(value.hasValue, value, showValues, row.iqc001Mostrarvalor, currentLanguage, row.iqc001Decimaispainel)}
						</OverflowSpan>
					</CardIndicatorPanel>
				))}
			</RCCGrid>
		</DivValues>
	);

	const customHeaderColAccumulatedValues = (rows: any) => {
		const lengths = rows.map((row: IListIndicators) => row.accumulatedValues && Array.isArray(row.accumulatedValues) ? row.accumulatedValues.length : 0);
		const maxLengthAccumulated = lengths.indexOf(Math.max(...lengths));
		const firstRow = rows[maxLengthAccumulated];
		return(
			<DivValues>
				<ArrowLeft className="arrowLeft-indicators">
					<RCCIcon 
						name="arrowLeft" 
						color="var(--default-color-font)" 
						size={32}
						onClick={() => handleScrollAccumulatedValues("left")} 
					/>
				</ArrowLeft>
				<RCCGrid 
					container
					item
					styleInline="flex-wrap: unset !important; padding: 0 4px;"
					className="indicators-accumulated-values-panel-cards"
				>
					{firstRow.accumulatedValues && firstRow.accumulatedValues.length > 0 && firstRow.accumulatedValues.map((value: IIndicatorsValues) => (
						<IndicatorsPanelHeader
							key={v4()}
							currentPeriod={!!(value.labelCode && value.currentPeriod && value.labelCode === value.currentPeriod)}
						>
							<OverflowSpan title={value.label}>
								{value.label}
							</OverflowSpan>
						</IndicatorsPanelHeader>
					))}
				</RCCGrid>
				<ArrowRight className="arrowRight-indicators">
					<RCCIcon 
						name="arrowRight" 
						color="var(--default-color-font)" 
						size={32}
						onClick={() => handleScrollAccumulatedValues("right")} 
					/>
				</ArrowRight>
			</DivValues>
		);};

	const listChild = (dt: any) => (
		<ListIndicators 
			data={dt.children}
			options={(row: IListIndicators) => options(row)}
			showValues={showValues}
			filterList={filterList}
			showHierarchyList
			loadChildrens={loadChildrens}
			openChartIndicators={openChartIndicators}
			openValuesOfPeriodIndicator={openValuesOfPeriodIndicator}
			childrenLevel={!childrenLevel ? 1 : childrenLevel + 1}
			defaultChecked={defaultChecked}
			mouseOutCardValuePanel={mouseOutCardValuePanel}
			mouseOverCardValuePanel={mouseOverCardValuePanel}
		/>
	);
    
	return(
		<>
			<DivFlex ref={divRef}>
				<List 
					data={data}
					columns={[{
						idColumn: "title",
						nameColumn: translate("Indicador"),
						xs: 12,
						sm: 12,
						md: 3,
						justify: "left",
						cellRenderer: colIndicator,
						customHeader: customHeaderColIndicator
					},
					{
						idColumn: "values",
						nameColumn: translate("Valores"),
						xs: 12,
						sm: 12,
						md: 7,
						cellRenderer: colValues,
						customHeader: customHeaderColValues
					},
					{
						idColumn: "accumulatedValues",
						nameColumn: translate("Valores Acumulados"),
						xs: 12,
						sm: 12,
						md: 2,
						cellRenderer: colAccumulatedValues,
						customHeader: customHeaderColAccumulatedValues
					},
					{
						idColumn: "options",
						nameColumn: " ",
						styleInline: "position: absolute; right: -12px; bottom: 0; width: fit-content !important;",
						hideHeader: true,
						cellRenderer: options
					}]}
					childrenOfLine={{
						cellRenderer: listChild,
						conditionToRender: (dt: any) => dt.children && dt.children.length > 0
					}}
					showHeader
					headerHiddenXs={false}
					layout="table"
					defaultNumberOfElements={20}
					manuallyToggleShowMore
					filter={filterList ? (indicator) => indicator.iqc001Nivel === filterList : undefined} 
					styleInline={`li.list-component-child {
						min-height: 42px;
						padding: 0;
						padding-right: 8px;
						flex-wrap: unset;
					}
						li.list-component-header{
							font-size: 0.8rem;
							padding: 0;
							padding-right: 8px;
							margin: 0;
							min-height: 26px;
							flex-wrap: unset;
						}
						@media(max-width: 959px){
						li.list-component-child{
							min-height: 96px;
						}
						>li{
							flex-direction: column !important;
							align-items: flex-start !important;
							div{
								justify-content: flex-start !important;
							}
						}
					}`}
				/>
			</DivFlex>
		</>
	);};

export default memo(ListIndicators);
